import { createSelector } from 'reselect'
import * as isNumber from 'lodash/isNumber'
import * as last from 'lodash/last'
import { State } from 'interfaces/state'
import { AccountBreakdownOwnProps, AccountType, AccountTyped, AllAccountsDetailedState, CollectionCategoryInterest, DetailedAccount } from 'interfaces/accounts'
import { TimeframeTyped } from 'interfaces/global'
import { PeergroupTrendCollection } from 'interfaces/trend'
import { AccountTrendOwnProps } from 'components/features/pfm/LineChart/AccountsTrendModuleContainer'
import { getMonthsCount, getTimeframeLastMonth } from 'helpers/dateTimeHelpers'
import { getCategoryInterests } from 'helpers/accountHelpers'
import { selectedPeergroupIdSelector, yourPeergroupIdSelector } from 'selectors/peergroupSelectors'
import { createOwnPropsSelector, metaCategoriesSelector, createSelectedCategoryIdSelector } from 'selectors/globalSelectors'
import { getItemsFromCollection } from 'helpers/normalizeHelpers'

// ===========================================================================//
//                                 BREAKDOWN                                  //
// ===========================================================================//

export const memberAccountBreakdownSelector = (state: State, { type, timeframe }: AccountBreakdownOwnProps) =>
  state.data[type].breakdown[getTimeframeLastMonth(timeframe)]

export const peergroupAccountBreakdownsSelector = (state: State, { type, timeframe }: AccountBreakdownOwnProps) =>
  state.data.peergroups[type].breakdown[getTimeframeLastMonth(timeframe)]

export const hasPeergroupAccountBreakdownsSelector = createSelector(
  peergroupAccountBreakdownsSelector,
  yourPeergroupIdSelector,
  selectedPeergroupIdSelector,
  (breakdowns, yoursId, othersId) =>
    Boolean(breakdowns) &&
      [yoursId, othersId].every((id) => isNumber(id) && Boolean(breakdowns.byKey[id]))
)

export const createPeergroupBreakdownSelector = (
  type: AccountType,
  peergroupSelector: (state: State) => number | null
) => createSelector(
  (state: State, { timeframe }: TimeframeTyped) => peergroupAccountBreakdownsSelector(state, { type, timeframe }),
  peergroupSelector,
  (breakdowns, peersId) =>
    isNumber(peersId) && breakdowns.byKey[peersId]
      ? breakdowns.byKey[peersId].breakdown
      : null
)

// ===========================================================================//
//                                  TRENDS                                    //
// ===========================================================================//

export const memberAccountTrendSelector = (state: State, ownProps: AccountTrendOwnProps) => {
  const { type, timeframe, categoryId } = ownProps
  return state.data[type].trends[timeframe][categoryId]
}

export const memberAccountRankCalculationValueSelector = createSelector(
  memberAccountTrendSelector,
  createOwnPropsSelector<AccountTrendOwnProps>(),
  (trend, { timeframe }) =>
    getMonthsCount(timeframe) === 1
      ? last(trend.actual).amount
      : trend.average
)

export const peergroupAccountTrendsSelector = (state: State, { type }: AccountTyped) =>
  state.data.peergroups[type].trends

const selectedCategoryIdSelector = createSelectedCategoryIdSelector()
export const peergroupsAccountTrendSelector = createSelector(
  peergroupAccountTrendsSelector,
  (state: State, { type }) => selectedCategoryIdSelector(state, type),
  (trends, category): PeergroupTrendCollection | null =>
    trends[category] || null
)

export const isPeergroupAccountTrendsLoadedSelector = createSelector(
  peergroupsAccountTrendSelector,
  yourPeergroupIdSelector,
  selectedPeergroupIdSelector,
  (trend, yourPeersId, selectedId) =>
    Boolean(
      trend
      && [yourPeersId, selectedId].every((id) => isNumber(id) && Boolean(trend.byKey[id]))
    )
)

export const yourPeersAccountTrendsSelector = createSelector(
  peergroupsAccountTrendSelector,
  yourPeergroupIdSelector,
  (trend, peersId) =>
    trend && isNumber(peersId)
      ? trend.byKey[peersId]
      : null
)

export const othersAccountTrendsSelector = createSelector(
  peergroupsAccountTrendSelector,
  selectedPeergroupIdSelector,
  (trend, otherId) =>
    trend && isNumber(otherId)
      ? trend.byKey[otherId]
      : null
)

// ===========================================================================//
//                                  TRENDS                                    //
// ===========================================================================//
export const allAccountsSelector = (state: State, { type }: AccountTyped): AllAccountsDetailedState =>
  state.data[type].accounts

export const ratesSelector = (state: State, { type, timeframe }: AccountTyped & TimeframeTyped) => {
  const rates = state.data[type].rates

  return rates
    ? rates[timeframe]
    : null
}

export const createRatedAccountsSelector = () =>
  createSelector(
    ratesSelector,
    allAccountsSelector,
    (rates, accounts) =>
      rates && accounts
        ? rates.allKeys
          .filter((id) => Boolean(accounts.provided.byKey[id]))
          .map((id) => ({
            ...accounts.provided.byKey[id],
            rateObj: rates.byKey[id],
          }))
        : null
  )

export const linkedRatedAccountsSelector = createSelector(
  createRatedAccountsSelector(),
  (ratedAccounts) =>
    ratedAccounts
      ? ratedAccounts.filter((account) => account.link_status !== 'unlinked')
      : null
)

export const manualLinkedDetailedAccountsSelector =
  createSelector(
    allAccountsSelector,
    (allAccounts) => allAccounts
      ? getItemsFromCollection<DetailedAccount, 'id'>(allAccounts.manual).filter(({ link_status }) => link_status !== 'unlinked')
      : null
  )

export const categoryInterestsSelector = createSelector(
  linkedRatedAccountsSelector,
  manualLinkedDetailedAccountsSelector,
  metaCategoriesSelector,
  (linkedRatedAccounts, manualAccounts, categories): CollectionCategoryInterest | null => {
    if (!linkedRatedAccounts) {
      return null
    }

    manualAccounts = manualAccounts || []
    const categoryInterests = getCategoryInterests([...linkedRatedAccounts, ...manualAccounts], categories)

    return categoryInterests
  }
)
